import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  RangoSalarialPerfil,
  CreateRangoSalarialPerfilRequest,
  UpdateRangoSalarialPerfilRequest
} from '../../models/rango-salarial-perfil.interface';
import { RANGO_SALARIAL_PERFIL_LABELS } from './rango-salarial-perfil.labels';
import * as RangoSalarialPerfilActions from '../../store/rango-salarial-perfil.actions';
import * as RangoSalarialPerfilSelectors from '../../store/rango-salarial-perfil.selectors';
import * as PerfilSelectors from '../../store/perfil.selectors';
import * as PerfilActions from '../../store/perfil.actions';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';

@Component({
  selector: 'app-rango-salarial-perfil',
  templateUrl: './rango-salarial-perfil.component.html',
  styleUrls: ['./rango-salarial-perfil.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatPaginatorModule
  ]
})
export class RangoSalarialPerfilComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = RANGO_SALARIAL_PERFIL_LABELS;

  // Observables del store
  rangosSalariales$ = this.store.select(RangoSalarialPerfilSelectors.selectAllRangosSalariales);
  perfiles$ = this.store.select(PerfilSelectors.selectAllPerfiles);
  loading$ = this.store.select(RangoSalarialPerfilSelectors.selectRangoSalarialPerfilLoading);
  error$ = this.store.select(RangoSalarialPerfilSelectors.selectRangoSalarialPerfilError);
  creating$ = this.store.select(RangoSalarialPerfilSelectors.selectRangoSalarialPerfilCreating);
  updating$ = this.store.select(RangoSalarialPerfilSelectors.selectRangoSalarialPerfilUpdating);

  // Filtros por columnas
  columnFilters: { perfil: string; ano: string; minimo: string; maximo: string } = {
    perfil: '',
    ano: '',
    minimo: '',
    maximo: ''
  };

  // Subjects para filtros
  searchSubject = new BehaviorSubject<string>('');
  columnFiltersSubject = new BehaviorSubject<typeof this.columnFilters>(this.columnFilters);
  filtersSubject = new BehaviorSubject<number>(0);

  // Observable combinado para datos filtrados
  displayRangosSalariales$ = combineLatest([
    this.rangosSalariales$,
    this.searchSubject,
    this.columnFiltersSubject
  ]).pipe(
    map(([rangosSalariales, globalSearch, colFilters]) => {
      let result = rangosSalariales;

      // Búsqueda general
      const globalQuery = (globalSearch || '').toLowerCase().trim();
      if (globalQuery) {
        result = result.filter(
          r =>
            (r.perfilNombre || '').toLowerCase().includes(globalQuery) ||
            r.ano.toString().includes(globalQuery) ||
            r.minimo.toString().includes(globalQuery) ||
            r.maximo.toString().includes(globalQuery)
        );
      }

      // Filtros por columna
      const { perfil, ano, minimo, maximo } = colFilters;
      if (perfil && perfil.trim()) {
        const q = perfil.toLowerCase().trim();
        result = result.filter(r => (r.perfilNombre || '').toLowerCase().includes(q));
      }
      if (ano && ano.trim()) {
        const q = ano.toLowerCase().trim();
        result = result.filter(r => r.ano.toString().includes(q));
      }
      if (minimo && minimo.trim()) {
        const q = minimo.toLowerCase().trim();
        result = result.filter(r => r.minimo.toString().includes(q));
      }
      if (maximo && maximo.trim()) {
        const q = maximo.toLowerCase().trim();
        result = result.filter(r => r.maximo.toString().includes(q));
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  pageIndex = 0;
  totalItems = 0;

  // Propiedades para búsqueda
  searchQuery = '';
  showSearchPanel = false;

  // Formulario unificado
  rangoSalarialForm: FormGroup;

  // Referencias a templates
  @ViewChild('createRangoSalarialModal') createRangoSalarialModal!: TemplateRef<unknown>;
  @ViewChild('paginator') set paginator(p: MatPaginator) {
    if (p) {
      this._paginator = p;
      this.dataSource.paginator = p;
    }
  }
  private _paginator!: MatPaginator;

  // Estado del componente
  isEditing = false;
  editingRangoSalarial: RangoSalarialPerfil | null = null;

  // Estados para rastrear operaciones
  wasCreating = false;
  wasUpdating = false;

  // Data source para la tabla
  dataSource = new MatTableDataSource<RangoSalarialPerfil>([]);

  // Columnas de la tabla
  displayedColumns: string[] = ['rangoSalarialId', 'perfil', 'ano', 'minimo', 'maximo', 'actions'];

  // TrackBy functions
  trackByRangoSalarialId = TrackByUtils.trackByProperty<RangoSalarialPerfil>('rangoSalarialId');

  // Keep latest rangos for validators
  private currentRangosSalariales: RangoSalarialPerfil[] = [];

  constructor() {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 10; // Allow up to 10 years in the future

    this.rangoSalarialForm = this.formBuilder.group(
      {
        perfilId: [null, [Validators.required]],
        ano: [null, [Validators.required, Validators.min(2000), Validators.max(maxYear)]],
        minimo: [null, [Validators.required, Validators.min(0)]],
        maximo: [null, [Validators.required, Validators.min(0)]]
      },
      {
        validators: [
          this.maximoGreaterThanMinimoValidator,
          this.duplicatePerfilAnoValidator.bind(this)
        ]
      }
    );
  }

  ngOnInit(): void {
    this.loadPerfiles();
    this.loadRangosSalariales();
    this.setupSubscriptions();
  }

  ngAfterViewInit(): void {
    // Conectar el paginador al data source inmediatamente
    this.dataSource.paginator = this._paginator;

    // Asegurar que el paginador esté configurado correctamente
    if (this._paginator) {
      this._paginator.pageSize = this.pageSize;
      this._paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Inicializar subjects de filtros
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ perfil: '', ano: '', minimo: '', maximo: '' });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    // Suscribirse a los cambios en displayRangosSalariales$ para actualizar el data source
    this.displayRangosSalariales$.pipe(takeUntil(this.destroy$)).subscribe(rangosSalariales => {
      // Actualizar el data source existente
      this.dataSource.data = rangosSalariales;

      // Asegurar que el paginador esté conectado
      if (this._paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this._paginator;
      }
    });

    // Suscribirse a todos los rangos salariales para validaciones
    this.rangosSalariales$.pipe(takeUntil(this.destroy$)).subscribe(rangosSalariales => {
      this.currentRangosSalariales = rangosSalariales;
      // Revalidate form when data changes
      this.rangoSalarialForm.updateValueAndValidity({ onlySelf: true });
    });

    // Suscripción a errores
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
        this.store.dispatch(RangoSalarialPerfilActions.clearRangoSalarialPerfilError());
      }
    });

    // Suscripción a creación exitosa
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Rango salarial creado exitosamente');
      }
      this.wasCreating = creating;
    });

    // Suscripción a actualización exitosa
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal('Rango salarial actualizado exitosamente');
      }
      this.wasUpdating = updating;
    });
  }

  private loadPerfiles(): void {
    this.store.dispatch(PerfilActions.loadPerfiles());
  }

  private loadRangosSalariales(): void {
    this.store.dispatch(RangoSalarialPerfilActions.loadRangosSalariales({ force: false }));
  }

  // Métodos de filtrado
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(this.searchQuery);
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  onColumnFilterChange(column: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[column] = value;
    this.columnFiltersSubject.next({ ...this.columnFilters });
    this.filtersSubject.next(Date.now());
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  clearFilters(): void {
    // Check if there are any active filters
    const hasActiveFilters =
      this.searchQuery !== '' ||
      this.columnFilters.perfil !== '' ||
      this.columnFilters.ano !== '' ||
      this.columnFilters.minimo !== '' ||
      this.columnFilters.maximo !== '';

    this.searchQuery = '';
    this.columnFilters = { perfil: '', ano: '', minimo: '', maximo: '' };
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ ...this.columnFilters });
    this.filtersSubject.next(Date.now());

    // Only go to first page if there were active filters
    if (hasActiveFilters && this._paginator) {
      this._paginator.firstPage();
    }
  }

  // Métodos de modal
  openCreateModal(rangoSalarial?: RangoSalarialPerfil): void {
    this.isEditing = !!rangoSalarial;
    this.editingRangoSalarial = rangoSalarial || null;

    if (this.isEditing && rangoSalarial) {
      this.rangoSalarialForm.patchValue({
        perfilId: rangoSalarial.perfilId,
        ano: rangoSalarial.ano,
        minimo: rangoSalarial.minimo,
        maximo: rangoSalarial.maximo
      });
    } else {
      this.rangoSalarialForm.reset();
    }

    this.dialog.open(this.createRangoSalarialModal, {
      width: '600px',
      maxWidth: '90vw',
      disableClose: false
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  resetForm(): void {
    this.rangoSalarialForm.reset();
    this.isEditing = false;
    this.editingRangoSalarial = null;
  }

  // Métodos de formulario
  async saveRangoSalarial(): Promise<void> {
    if (this.rangoSalarialForm.valid) {
      const formData = this.rangoSalarialForm.value;

      if (this.isEditing && this.editingRangoSalarial) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar este rango salarial?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          const updateRequest: UpdateRangoSalarialPerfilRequest = {
            perfilId: formData.perfilId,
            ano: formData.ano,
            minimo: formData.minimo,
            maximo: formData.maximo
          };

          this.store.dispatch(
            RangoSalarialPerfilActions.updateRangoSalarialPerfil({
              id: this.editingRangoSalarial.rangoSalarialId,
              rangoSalarial: updateRequest
            })
          );

          this.closeModal();
        }
      } else {
        // Para crear, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear este rango salarial?',
          'Confirmar Creación'
        );

        if (confirmed) {
          const createRequest: CreateRangoSalarialPerfilRequest = {
            perfilId: formData.perfilId,
            ano: formData.ano,
            minimo: formData.minimo,
            maximo: formData.maximo
          };

          this.store.dispatch(
            RangoSalarialPerfilActions.createRangoSalarialPerfil({ rangoSalarial: createRequest })
          );

          this.closeModal();
        }
      }
    } else {
      this.notificationService.showErrorModal(
        'Error de validación',
        'Por favor complete todos los campos requeridos'
      );
    }
  }

  // Validadores personalizados
  private maximoGreaterThanMinimoValidator(form: FormGroup) {
    const minimo = form.get('minimo')?.value;
    const maximo = form.get('maximo')?.value;

    if (minimo != null && maximo != null && maximo <= minimo) {
      return { maximoGreaterThanMinimo: true };
    }

    return null;
  }

  private duplicatePerfilAnoValidator(form: FormGroup) {
    const perfilId = form.get('perfilId')?.value;
    const ano = form.get('ano')?.value;

    if (perfilId == null || ano == null) {
      return null;
    }

    // Check if exists in current rangos (excluding the one being edited)
    const exists = this.currentRangosSalariales.some(
      r =>
        r.perfilId === perfilId &&
        r.ano === ano &&
        (!this.isEditing || r.rangoSalarialId !== this.editingRangoSalarial?.rangoSalarialId)
    );

    return exists ? { duplicatePerfilAno: true } : null;
  }

  // Métodos de validación
  hasError(controlName: string): boolean {
    const control = this.rangoSalarialForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(controlName: string): string {
    const control = this.rangoSalarialForm.get(controlName);
    if (control && control.errors) {
      if (control.errors['required']) {
        return this.labels.requiredField;
      }
      if (control.errors['min']) {
        switch (controlName) {
          case 'ano':
            return this.labels.anoMinValue;
          case 'minimo':
            return this.labels.minimoMinValue;
          case 'maximo':
            return this.labels.maximoMinValue;
        }
      }
      if (control.errors['max']) {
        if (controlName === 'ano') {
          return this.labels.anoMaxValue;
        }
      }
    }
    return '';
  }

  getFormError(): string {
    if (this.rangoSalarialForm.hasError('maximoGreaterThanMinimo')) {
      return this.labels.maximoGreaterThanMinimo;
    }
    if (this.rangoSalarialForm.hasError('duplicatePerfilAno')) {
      return this.labels.duplicatePerfilAno;
    }
    return '';
  }

  // Métodos de paginación
  onPageChange(): void {
    // La paginación se maneja automáticamente por MatPaginator
  }

  // Métodos de utilidad
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  }
}
